import React, { useState, useEffect } from 'react';
import API from '../api';
import { toast } from 'react-hot-toast';
import { useTranslation } from '../hooks/useTranslation';

// Timezone helper - display in GMT+7 (Asia/Bangkok)
const formatDateTime = (dateTime) => {
    if (!dateTime) return '-';
    const date = new Date(dateTime);
    return date.toLocaleString('en-GB', {
        timeZone: 'Asia/Bangkok',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    });
};

export default function EventsTable() {
    const { translate } = useTranslation();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        event_types: [],
        gate_ids: [],
        statuses: []
    });
    const [dateRange, setDateRange] = useState({
        start: new Date().toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
    });
    const [selectedFilters, setSelectedFilters] = useState({
        event_type: '',
        gate_id: '',
        status: ''
    });
    const [stats, setStats] = useState(null);

    const fetchEvents = async () => {
        setLoading(true);
        try {
            const params = {
                start_date: dateRange.start,
                end_date: dateRange.end,
                limit: 500
            };

            // Add optional filters
            if (selectedFilters.event_type) params.event_type = selectedFilters.event_type;
            if (selectedFilters.gate_id) params.gate_id = selectedFilters.gate_id;
            if (selectedFilters.status) params.status = selectedFilters.status;

            console.log('📋 Fetching events with params:', params);

            const response = await API.get('/events/barrier-events', { params });

            setEvents(response.data.events || []);
            setFilters(response.data.filters || { event_types: [], gate_ids: [], statuses: [] });

            toast.success(`Loaded ${response.data.events?.length || 0} events`);
        } catch (error) {
            console.error('Error fetching events:', error);
            toast.error('Failed to load events');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await API.get('/events/barrier-events/stats', {
                params: {
                    start_date: dateRange.start,
                    end_date: dateRange.end
                }
            });
            setStats(response.data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    useEffect(() => {
        fetchEvents();
        fetchStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange]);

    const handleSearch = () => {
        fetchEvents();
        fetchStats();
    };

    const getStatusColor = (status) => {
        switch (status?.toUpperCase()) {
            case 'RECEIVED':
                return 'bg-blue-100 text-blue-800';
            case 'PROCESSED':
                return 'bg-green-100 text-green-800';
            case 'FAILED':
                return 'bg-red-100 text-red-800';
            case 'PENDING':
                return 'bg-yellow-100 text-yellow-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getEventTypeColor = (eventType) => {
        switch (eventType?.toLowerCase()) {
            case 'entry':
                return 'bg-green-100 text-green-800';
            case 'exit':
                return 'bg-orange-100 text-orange-800';
            case 'barrier_open':
                return 'bg-blue-100 text-blue-800';
            case 'manual_open':
                return 'bg-purple-100 text-purple-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const handleExport = async () => {
        try {
            const params = {
                start_date: dateRange.start,
                end_date: dateRange.end,
                limit: 10000 // High limit for export
            };

            // Add optional filters
            if (selectedFilters.event_type) params.event_type = selectedFilters.event_type;
            if (selectedFilters.gate_id) params.gate_id = selectedFilters.gate_id;
            if (selectedFilters.status) params.status = selectedFilters.status;

            toast.loading('⏳ Generating Excel report...', { id: 'export-loading' });

            const response = await API.get('/events/barrier-events', { params });
            const eventsData = response.data.events || [];

            if (eventsData.length === 0) {
                toast.dismiss('export-loading');
                toast.error('No data to export');
                return;
            }

            // Dynamically import xlsx and file-saver
            const XLSX = await import('xlsx');
            const { saveAs } = await import('file-saver');

            // Format data for Excel
            const exportData = eventsData.map((event, index) => ({
                'No.': index + 1,
                'Event ID': event.id,
                'Gate ID': event.gate_id || '-',
                'Event Type': event.event_type || '-',
                'Plate Number': event.plate_number || '-',
                'Plate Color': event.plate_color || '-',
                'Plate Type': event.plate_type || '-',
                'Vehicle Brand': event.vehicle_brand || '-',
                'Vehicle Model': event.vehicle_model || '-',
                'Vehicle Type': event.vehicle_type || '-',
                'Vehicle Color': event.vehicle_color || '-',
                'Vehicle Kind': event.vehicle_kind || '-',
                'Channel': event.channel || '-',
                'Date Time': formatDateTime(event.timestamp),
                'Status': event.status || '-',
                'Message': event.message || '-',
                'UHF Card ID': event.uhf_card_id || '-',
                'OBU ID': event.obu_id || '-',
                'OBU SN': event.obu_sn || '-',
                'Lane': event.lane_number || '-',
                'Weight (kg)': event.vehicle_weight || '-',
                'Axles': event.axle_count || '-',
                'Transaction ID': event.transaction_id || '-'
            }));

            // Create worksheet
            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Barrier Events');

            // Generate Excel file
            const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

            // Save file
            const fileName = `barrier_events_${dateRange.start}_to_${dateRange.end}.xlsx`;
            saveAs(blob, fileName);

            toast.dismiss('export-loading');
            toast.success('✅ Export successful');
        } catch (error) {
            console.error('Export error:', error);
            toast.dismiss('export-loading');
            toast.error('Failed to export data');
        }
    };

    return (
        <div className="p-4 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                    📋 {translate('events.title') || 'Barrier Events'}
                </h1>
                <button
                    onClick={handleExport}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
                >
                    <span>📊</span>
                    <span>Export to Excel</span>
                </button>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow border-l-4 border-blue-500">
                        <div className="text-2xl font-bold text-blue-600">{stats.total_events || 0}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Total Events</div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow border-l-4 border-green-500">
                        <div className="text-2xl font-bold text-green-600">{stats.unique_gates || 0}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Gates</div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow border-l-4 border-purple-500">
                        <div className="text-2xl font-bold text-purple-600">{stats.received_count || 0}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Received</div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow border-l-4 border-green-500">
                        <div className="text-2xl font-bold text-green-600">{stats.processed_count || 0}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Processed</div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow border-l-4 border-red-500">
                        <div className="text-2xl font-bold text-red-600">{stats.failed_count || 0}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Failed</div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow border-l-4 border-orange-500">
                        <div className="text-2xl font-bold text-orange-600">{stats.with_plate || 0}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">With Plate</div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow border-l-4 border-indigo-500">
                        <div className="text-2xl font-bold text-indigo-600">{stats.unique_event_types || 0}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Event Types</div>
                    </div>
                </div>
            )}

            {/* Filter Controls */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                    {/* Date Range */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Start Date
                        </label>
                        <input
                            type="date"
                            value={dateRange.start}
                            onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            End Date
                        </label>
                        <input
                            type="date"
                            value={dateRange.end}
                            onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>

                    {/* Gate Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Gate ID
                        </label>
                        <select
                            value={selectedFilters.gate_id}
                            onChange={(e) => setSelectedFilters(prev => ({ ...prev, gate_id: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        >
                            <option value="">All Gates</option>
                            {filters.gate_ids.map(gateId => (
                                <option key={gateId} value={gateId}>{gateId}</option>
                            ))}
                        </select>
                    </div>

                    {/* Event Type Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Event Type
                        </label>
                        <select
                            value={selectedFilters.event_type}
                            onChange={(e) => setSelectedFilters(prev => ({ ...prev, event_type: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        >
                            <option value="">All Types</option>
                            {filters.event_types.map(type => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>

                    {/* Status Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Status
                        </label>
                        <select
                            value={selectedFilters.status}
                            onChange={(e) => setSelectedFilters(prev => ({ ...prev, status: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        >
                            <option value="">All Status</option>
                            {filters.statuses.map(status => (
                                <option key={status} value={status}>{status}</option>
                            ))}
                        </select>
                    </div>

                    {/* Search Button */}
                    <div className="flex items-end">
                        <button
                            onClick={handleSearch}
                            disabled={loading}
                            className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                        >
                            {loading ? '⏳ Loading...' : '🔍 Search'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Events Table */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr className="bg-gray-50 dark:bg-gray-700">
                                <th className="border-b dark:border-gray-600 p-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    No.
                                </th>
                                <th className="border-b dark:border-gray-600 p-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    Event ID
                                </th>
                                <th className="border-b dark:border-gray-600 p-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    Gate ID
                                </th>
                                <th className="border-b dark:border-gray-600 p-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    Event Type
                                </th>
                                <th className="border-b dark:border-gray-600 p-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    Plate Number
                                </th>
                                <th className="border-b dark:border-gray-600 p-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    Vehicle Info
                                </th>
                                <th className="border-b dark:border-gray-600 p-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    Channel
                                </th>
                                <th className="border-b dark:border-gray-600 p-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    Timestamp
                                </th>
                                <th className="border-b dark:border-gray-600 p-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    Status
                                </th>
                                <th className="border-b dark:border-gray-600 p-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    Message
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="8" className="p-8 text-center text-gray-500 dark:text-gray-400">
                                        <div className="animate-pulse">⏳ Loading events...</div>
                                    </td>
                                </tr>
                            ) : events.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="p-8 text-center text-gray-500 dark:text-gray-400">
                                        📭 No events found for the selected date range
                                    </td>
                                </tr>
                            ) : (
                                events.map((event, index) => (
                                    <tr
                                        key={event.id}
                                        className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                    >
                                        <td className="border-b dark:border-gray-600 p-3 text-sm text-gray-600 dark:text-gray-400">
                                            {index + 1}
                                        </td>
                                        <td className="border-b dark:border-gray-600 p-3 text-sm font-mono text-gray-800 dark:text-gray-200">
                                            {event.id}
                                        </td>
                                        <td className="border-b dark:border-gray-600 p-3 text-sm font-medium text-gray-800 dark:text-gray-200">
                                            {event.gate_id || '-'}
                                        </td>
                                        <td className="border-b dark:border-gray-600 p-3 text-center">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEventTypeColor(event.event_type)}`}>
                                                {event.event_type || '-'}
                                            </span>
                                        </td>
                                        <td className="border-b dark:border-gray-600 p-3 text-sm font-bold text-gray-800 dark:text-gray-200">
                                            <div className="flex flex-col">
                                                <span>{event.plate_number || '-'}</span>
                                                {(event.plate_color || event.plate_type) && (
                                                    <span className="text-[10px] font-normal text-gray-500">
                                                        {event.plate_color} {event.plate_type}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="border-b dark:border-gray-600 p-3 text-sm text-gray-600 dark:text-gray-400">
                                            <div className="flex flex-col max-w-[150px]">
                                                <span className="font-semibold text-gray-800 dark:text-gray-200 truncate">
                                                    {event.vehicle_brand} {event.vehicle_model}
                                                </span>
                                                <span className="text-xs truncate">
                                                    {event.vehicle_type} {event.vehicle_color && `| ${event.vehicle_color}`}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="border-b dark:border-gray-600 p-3 text-center text-sm text-gray-600 dark:text-gray-400">
                                            {event.channel || '-'}
                                        </td>
                                        <td className="border-b dark:border-gray-600 p-3 text-sm text-gray-600 dark:text-gray-400">
                                            {formatDateTime(event.timestamp)}
                                        </td>
                                        <td className="border-b dark:border-gray-600 p-3 text-center">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(event.status)}`}>
                                                {event.status || '-'}
                                            </span>
                                        </td>
                                        <td className="border-b dark:border-gray-600 p-3 text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate" title={event.message}>
                                            {event.message || '-'}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer */}
                {events.length > 0 && (
                    <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 border-t dark:border-gray-600">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Showing <strong>{events.length}</strong> events
                            {dateRange.start && dateRange.end && (
                                <span> from <strong>{dateRange.start}</strong> to <strong>{dateRange.end}</strong></span>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
